import { z } from 'zod';
import {
  IsoTimestampSchema,
  TenantIdSchema,
  UlidSchema,
} from './common.js';

// ──────────────────────────────────────────────────────────────────────────
// Enums
// ──────────────────────────────────────────────────────────────────────────

export const TipoAsambleaSchema = z.enum(['ordinaria', 'extraordinaria']);
export type TipoAsamblea = z.infer<typeof TipoAsambleaSchema>;

export const EstadoAsambleaSchema = z.enum([
  'borrador',
  'convocada',
  'en_votacion',
  'cerrada',
  'sin_quorum',
  'cerrada_sin_efecto',
  'anulada',
]);
export type EstadoAsamblea = z.infer<typeof EstadoAsambleaSchema>;

export const CategoriaMateriaSchema = z.enum([
  'informativa',
  'gestion_simple',
  'aprobacion_gasto',
  'obra_mayor',
  'modificacion_reglamento',
  'cambio_destino',
  'eleccion_cargos',
]);
export type CategoriaMateria = z.infer<typeof CategoriaMateriaSchema>;

export const TipoVotoSchema = z.enum(['si_no', 'opciones_multiples', 'eleccion_cargos']);
export type TipoVoto = z.infer<typeof TipoVotoSchema>;

export const MayoriaRequeridaSchema = z.enum(['simple', 'calificada_75', 'unanimidad']);
export type MayoriaRequerida = z.infer<typeof MayoriaRequeridaSchema>;

export const ResultadoSchema = z.enum(['aprobada', 'rechazada', 'sin_quorum']);
export type ResultadoMateria = z.infer<typeof ResultadoSchema>;

export const RolFirmaSchema = z.enum(['presidente', 'administrador']);
export type RolFirma = z.infer<typeof RolFirmaSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Materia (Value Object inline en Asamblea)
// ──────────────────────────────────────────────────────────────────────────

export const MateriaSchema = z.object({
  id: UlidSchema,
  titulo: z.string().min(3).max(200),
  descripcion: z.string().max(2000).optional(),
  categoria: CategoriaMateriaSchema,
  tipoVoto: TipoVotoSchema,
  /** Only when tipoVoto !== 'si_no' */
  opciones: z.array(z.string().min(1).max(200)).optional(),
  /** Only when tipoVoto === 'eleccion_cargos' */
  cuposDisponibles: z.number().int().positive().optional(),
  mayoriaRequerida: MayoriaRequeridaSchema.nullable(),
  orden: z.number().int().nonnegative(),
});
export type Materia = z.infer<typeof MateriaSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Override (plazo/quorum/mayoría)
// ──────────────────────────────────────────────────────────────────────────

export const OverrideSchema = z.object({
  tipo: z.enum(['plazo_convocatoria', 'quorum_constitucion', 'mayoria_materia']),
  materiaId: UlidSchema.optional(),
  valorOriginal: z.union([z.number(), z.string()]),
  valorNuevo: z.union([z.number(), z.string()]),
  razon: z.string().min(10).max(500),
});
export type Override = z.infer<typeof OverrideSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Asamblea
// ──────────────────────────────────────────────────────────────────────────

export const AsambleaSchema = z.object({
  id: UlidSchema,
  tenantId: TenantIdSchema,
  tipo: TipoAsambleaSchema,
  numeroCitacion: z.union([z.literal(1), z.literal(2)]),
  estado: EstadoAsambleaSchema,
  fechaProgramada: IsoTimestampSchema,
  lugar: z.string().min(3).max(500),
  plazoVotacionDesde: IsoTimestampSchema,
  plazoVotacionHasta: IsoTimestampSchema,
  quorumConstitucionRequerido: z.number().min(0).max(1),
  materias: z.array(MateriaSchema),
  overrides: z.array(OverrideSchema).default([]),
  asambleaPadreId: UlidSchema.optional(),
  presidenteId: UlidSchema,
  creadoPor: z.string().min(1),
  creadoEn: IsoTimestampSchema,
  actualizadoEn: IsoTimestampSchema,
  version: z.number().int().nonnegative(),
});
export type Asamblea = z.infer<typeof AsambleaSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Voto
// ──────────────────────────────────────────────────────────────────────────

export const VotoSchema = z.object({
  tenantId: TenantIdSchema,
  asambleaId: UlidSchema,
  materiaId: UlidSchema,
  unidadId: UlidSchema,
  propietarioId: UlidSchema,
  /** string for si_no/opciones_multiples; string[] for eleccion_cargos */
  opcion: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]),
  alicuotaPonderada: z.number().min(0).max(1),
  registradoEn: IsoTimestampSchema,
});
export type Voto = z.infer<typeof VotoSchema>;

// ──────────────────────────────────────────────────────────────────────────
// ResultadoMateria (materialized tally)
// ──────────────────────────────────────────────────────────────────────────

export const ConteoOpcionSchema = z.object({
  cantidadVotos: z.number().int().nonnegative(),
  alicuotaAcumulada: z.number().min(0),
});
export type ConteoOpcion = z.infer<typeof ConteoOpcionSchema>;

export const ResultadoMateriaEntitySchema = z.object({
  tenantId: TenantIdSchema,
  asambleaId: UlidSchema,
  materiaId: UlidSchema,
  conteoPorOpcion: z.record(z.string(), ConteoOpcionSchema),
  alicuotaParticipante: z.number().min(0),
  alicuotaTotalCondominio: z.number().min(0),
  mayoriaRequerida: MayoriaRequeridaSchema,
  resultado: ResultadoSchema,
  computadoEn: IsoTimestampSchema,
});
export type ResultadoMateriaEntity = z.infer<typeof ResultadoMateriaEntitySchema>;

// ──────────────────────────────────────────────────────────────────────────
// Acta
// ──────────────────────────────────────────────────────────────────────────

export const EstadoActaSchema = z.enum(['borrador', 'firmada']);
export type EstadoActa = z.infer<typeof EstadoActaSchema>;

export const AdjuntoActaSchema = z.object({
  id: UlidSchema,
  s3Key: z.string().min(1),
  nombre: z.string().min(1).max(255),
  contentType: z.string().min(1).max(128),
  subidoEn: IsoTimestampSchema,
});
export type AdjuntoActa = z.infer<typeof AdjuntoActaSchema>;

export const FirmaActaSchema = z.object({
  rol: RolFirmaSchema,
  propietarioId: UlidSchema,
  feaReferenceMock: z.string().min(1),
  firmadoEn: IsoTimestampSchema,
});
export type FirmaActa = z.infer<typeof FirmaActaSchema>;

export const ActaSchema = z.object({
  tenantId: TenantIdSchema,
  asambleaId: UlidSchema,
  estado: EstadoActaSchema,
  textoLibre: z.string().max(50000).default(''),
  /** snapshot serializable del cómputo al cerrar votación */
  tallyCongelado: z.unknown(),
  adjuntos: z.array(AdjuntoActaSchema).default([]),
  firmas: z.array(FirmaActaSchema).default([]),
  pdfS3Key: z.string().nullable(),
  version: z.number().int().nonnegative(),
  actualizadoEn: IsoTimestampSchema,
});
export type Acta = z.infer<typeof ActaSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Request / Response DTOs
// ──────────────────────────────────────────────────────────────────────────

export const CrearAsambleaRequestSchema = z.object({
  tipo: TipoAsambleaSchema,
  fechaProgramada: IsoTimestampSchema,
  lugar: z.string().min(3).max(500),
  plazoVotacionDesde: IsoTimestampSchema,
  plazoVotacionHasta: IsoTimestampSchema,
  presidenteId: UlidSchema,
});
export type CrearAsambleaRequest = z.infer<typeof CrearAsambleaRequestSchema>;

export const ActualizarBorradorAsambleaRequestSchema = CrearAsambleaRequestSchema.partial();
export type ActualizarBorradorAsambleaRequest = z.infer<
  typeof ActualizarBorradorAsambleaRequestSchema
>;

export const AgregarMateriaRequestSchema = MateriaSchema.omit({ id: true, orden: true, mayoriaRequerida: true });
export type AgregarMateriaRequest = z.infer<typeof AgregarMateriaRequestSchema>;

export const PublicarAsambleaRequestSchema = z.object({
  override: z
    .object({
      razon: z.string().min(10).max(500),
    })
    .optional(),
});
export type PublicarAsambleaRequest = z.infer<typeof PublicarAsambleaRequestSchema>;

export const ExtenderVotacionRequestSchema = z.object({
  nuevoFin: IsoTimestampSchema,
});
export type ExtenderVotacionRequest = z.infer<typeof ExtenderVotacionRequestSchema>;

export const AnularAsambleaRequestSchema = z.object({
  razon: z.string().min(10).max(500),
});
export type AnularAsambleaRequest = z.infer<typeof AnularAsambleaRequestSchema>;

export const RegistrarVotoRequestSchema = z.object({
  unidadId: UlidSchema,
  opcion: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]),
});
export type RegistrarVotoRequest = z.infer<typeof RegistrarVotoRequestSchema>;

export const FirmarActaRequestSchema = z.object({
  rol: RolFirmaSchema,
});
export type FirmarActaRequest = z.infer<typeof FirmarActaRequestSchema>;

export const SubirAdjuntoRequestSchema = z.object({
  nombre: z.string().min(1).max(255),
  contentType: z.string().min(1).max(128),
});
export type SubirAdjuntoRequest = z.infer<typeof SubirAdjuntoRequestSchema>;

export const SubirAdjuntoResponseSchema = z.object({
  adjuntoId: UlidSchema,
  s3Key: z.string(),
  uploadUrl: z.url(),
  expiresAt: IsoTimestampSchema,
});
export type SubirAdjuntoResponse = z.infer<typeof SubirAdjuntoResponseSchema>;

export const DescargarActaResponseSchema = z.object({
  signedUrl: z.url(),
  expiresAt: IsoTimestampSchema,
});
export type DescargarActaResponse = z.infer<typeof DescargarActaResponseSchema>;

// Legacy compat (downstream code may still reference these names in existing scaffolding;
// we keep placeholders for mechanical renames in later tasks).
export const PuntoOrdenDiaSchema = MateriaSchema;
export type PuntoOrdenDia = Materia;
